import draw from "@core/draw";
import { FSRSCard, knolToFSRSCard } from "@core/fsrs/scheduler";
import { shuffle } from "@core/shuffle";
import idb from "@data/idb";
import { MapLike } from "@lib/mapView";
import { IDeck } from "@models/deck";
import { isDeckFSRS } from "@models/deckSettings";
import { IDeckSettings, Orientation, cardsPerReviewAllValue } from "@models/deckSettings";
import { IKnol } from "@models/knol";
import { ILayout } from "@models/layout";
import { ALL_LAYOUT_ID, MAGIC_LAYOUT_ID } from "fields/magicLayout";
import { ID } from "types/ID";
import { genTutorialKnol, shouldShowIntroSlide } from "./introSlide";
import { getCardDataWithOptions } from "./lib";
import { loadBlobIDsForCard, templatesForDeck } from "./reviewSessionBlobs";

const firstReviewSessionSize = 5; // Not including the tutorial card.

function sortFSRSCardsByDueDate(cards: FSRSCard[]): FSRSCard[] {
  return cards.sort((a, b) => {
    // Due cards come first
    if (a.state === "New" && b.state !== "New") return 1;
    if (a.state !== "New" && b.state === "New") return -1;
    // Otherwise sort by due date
    return a.due - b.due;
  });
}

function uniformSample<T>(array: T[]): T | undefined {
  if (array.length === 0) {
    return undefined;
  }

  const randomIndex: number = Math.floor(Math.random() * array.length);
  return array[randomIndex];
}

export interface IReviewSession {
  settings: IDeckSettings;
  knols: IKnol[];
  knolMap: MapLike<ID, IKnol>;
  decks: MapLike<ID, IDeck>;
  knolIDToLayout: Map<ID, ILayout>;
  cardOrientations: Record<ID, Orientation>;
}

export async function initReviewSession({
  decks,
  knols,
  settings,
}: {
  decks: MapLike<ID, IDeck>;
  knols: MapLike<ID, IKnol>;
  settings: IDeckSettings;
}): Promise<IReviewSession> {
  // Select from knols according to settings.
  const cardData = await getCardDataWithOptions(knols.values(), settings);
  const allCards = Array.from(cardData);

  let cards: IKnol[] = [];
  if (navigator.onLine) {
    // Draw from all cards. We'll download blobs on-demand if necessary.
    cards = allCards;
  } else {
    // Restrict drawing to only those cards with all blobs local.
    const tx = idb.blobs.transaction(["blobs"], "readwrite");
    const blobsStore = tx.objectStore("blobs");
    for (const knol of allCards) {
      if (!knol.values) {
        continue;
      }
      const deckID = knol.deck_id;
      const deck = decks.get(deckID);
      if (!deck) {
        continue;
      }
      const ids = await loadBlobIDsForCard(
        knol.deck_id,
        knol.values,
        deck?.config?.fields ?? [],
        templatesForDeck(deck),
      );
      const { queuedBlobs } = ids;

      let allBlobsLocal = true;
      for (const { id } of queuedBlobs) {
        const needToDownloadBlob = !(await blobsStore.getKey(id));
        if (needToDownloadBlob) {
          allBlobsLocal = false;
          break;
        }
      }
      if (allBlobsLocal) {
        cards.push(knol);
      }
    }
    await tx.done;
  }

  // For Omni Review, if any of the decks are set to FSRS, act like in FSRS mode.
  let anyDeckIsFSRS = false;
  for (const deck of decks.values()) {
    if (isDeckFSRS(deck)) {
      anyDeckIsFSRS = true;
    }
  }
  const isHybridFSRS = anyDeckIsFSRS;

  const { reviewMode, cardsPerReview, cardOrientation: deckOrientation } = settings;

  const numCards = cardsPerReview === cardsPerReviewAllValue ? cards.length : cardsPerReview;
  const maxCards = Math.min(cards.length, numCards);

  if (isHybridFSRS || reviewMode.name === "fsrs") {
    const fsrsCards = cards.map((knol) => knolToFSRSCard(knol)).filter((c): c is FSRSCard => !!c);

    // If FSRS due date filters are applied, use all filtered cards instead of only currently due cards
    const hasDueDateFilters = settings.fsrsDueDateFilter && settings.fsrsDueDateFilter.length > 0;
    if (hasDueDateFilters) {
      // Sort by due date and take up to maxCards
      // Due cards should appear first, then new cards
      const sortedCards = sortFSRSCardsByDueDate(fsrsCards);
      const knolIDs = sortedCards.slice(0, maxCards).map((c) => c.id);
      cards = knolIDs
        .map((id) => knols.get(id))
        .filter((knol): knol is IKnol => knol !== undefined);
    } else {
      // No date filters.
      // If due or new cards, draw those.
      // Otherwise, draw from all cards.
      const dueCards = fsrsCards.filter((c) => c.due <= Date.now() || c.state === "New");
      const candidateCards = dueCards.length > 0 ? dueCards : fsrsCards;
      const sortedCards = sortFSRSCardsByDueDate(candidateCards);
      const knolIDs = sortedCards.slice(0, maxCards).map((c) => c.id);
      cards = knolIDs
        .map((id) => knols.get(id))
        .filter((knol): knol is IKnol => knol !== undefined);
    }
  } else if (reviewMode.name === "shuffled") {
    cards = shuffle(cards);
    cards = cards.slice(0, maxCards);
  } else if (reviewMode.name === "in_created_order") {
    cards = cards.sort((a, b) => {
      if (!a.created_at || !b.created_at) {
        return 0;
      }
      return a.created_at.getTime() - b.created_at.getTime();
    });
    const skip = reviewMode.skip;
    const min = Math.min(skip, cards.length);
    cards = cards.slice(min, maxCards);
  } else {
    // reviewMode.name === "srs" or some unrecognized value.
    const knolIDs = draw(cards, maxCards);
    cards = knolIDs.map((id) => knols.get(id)).filter((knol): knol is IKnol => knol !== undefined);
  }

  const layoutMap = new Map<ID, ILayout>();
  for (const deck of decks.values()) {
    for (const layout of deck.layouts ?? []) {
      layoutMap.set(layout.id, layout);
    }
  }

  // Determine layout to use for each card.
  const knolIDToLayout = new Map<ID, ILayout>();
  for (const knol of knols.values()) {
    const deck = decks.get(knol.deck_id);
    const layoutID = deck?.layout_id;
    if (layoutID === ALL_LAYOUT_ID) {
      // Pick a random one.
      const layout = uniformSample(deck?.layouts ?? []);
      if (layout) {
        knolIDToLayout.set(knol.id, layout);
      }
    } else {
      if (layoutID) {
        const layout = layoutMap.get(layoutID);
        if (layout) {
          knolIDToLayout.set(knol.id, layout);
        }
      }
    }
  }

  // Determine card orientations.
  const cardOrientations: Record<ID, Orientation> = {};
  for (const id of cards.map((c) => c.id)) {
    switch (deckOrientation) {
      case "normal":
      case "reversed":
        cardOrientations[id] = deckOrientation;
        break;
      case "mixed":
        cardOrientations[id] = Math.random() >= 0.5 ? "normal" : "reversed";
    }
  }

  // Create tutorial card for first-time users
  const isFirstTimeUser = shouldShowIntroSlide();
  if (isFirstTimeUser && decks.size > 0) {
    const firstDeck = Array.from(decks.values())[0];
    const tutorialKnol = genTutorialKnol(firstDeck.id);
    cards.unshift(tutorialKnol);
    const layout = layoutMap.get(MAGIC_LAYOUT_ID);
    if (layout) {
      knolIDToLayout.set(tutorialKnol.id, layout);
    }
    cardOrientations[tutorialKnol.id] = "normal";

    // Add tutorial knol to the knolMap
    const extendedKnolMap = new Map(knols);
    extendedKnolMap.set(tutorialKnol.id, tutorialKnol);
    knols = extendedKnolMap;

    cards = cards.slice(0, firstReviewSessionSize + 1);
  }

  const session: IReviewSession = {
    settings,
    decks,
    knols: cards,
    knolMap: knols,
    knolIDToLayout,
    cardOrientations,
  };

  return session;
}
